import React, { useEffect, useState, useCallback } from 'react'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import Shelf from '../shelf'
import Carousel from 'components/modular/carousel'
import {
  carouselBookSettings,
  carouselAudiobookSettings,
  carouselHighlightedBooksSettings,
  carouselHighlightedAudiobookSettings,
  carouselNewsReelSettings,
  carouselHighlightedNewsReelSettings
} from './BookShelf.constants'
import Book from 'components/modular/book'
import type { BookShelfProps } from './BookShelf.types'
import ShelfLoading from 'components/modular/shelf-loading'
import { post_event } from 'utils/analytics'
import { useUserInfo } from 'utils/hooks/use-userInfo'

const BookShelf: React.FC<BookShelfProps> = ({
  loading,
  title,
  subtitle,
  paddingRight,
  disableHeader,
  contentFilter,
  onClickBtnGetMore,
  isSeeAllButtonVisible = true,
  format = 'book',
  coversList,
  size = 'large',
  showInfo,
  onClickCover,
  dataTestId,
  carouselSettings
}) => {
  const { user } = useUserInfo()

  const [spaceBetweenItems, setSpaceBetweenItems] = useState<string>(
    tokens.spacing.horizontalLarge
  )
  const handleResize = useCallback(() => {
    if (window.innerWidth >= tokens.sizes.mdScreen) {
      setSpaceBetweenItems(tokens.spacing.horizontalLarge)
    } else {
      setSpaceBetweenItems(tokens.spacing.horizontalMedium)
    }
  }, [])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize, false)

    return () => {
      window.removeEventListener('resize', handleResize, false)
    }
  }, [handleResize])

  const setSettings = {
    book: {
      large: carouselBookSettings,
      xlarge: carouselHighlightedBooksSettings
    },
    audiobook: {
      large: carouselAudiobookSettings,
      xlarge: carouselHighlightedAudiobookSettings
    },
    newsreel: {
      large: carouselNewsReelSettings,
      xlarge: carouselHighlightedNewsReelSettings
    }
  }

  if (loading) return <ShelfLoading height={400} />

  return coversList && coversList.length > 0 ? (
    <Shelf
      title={title}
      subtitle={subtitle}
      paddingRight={paddingRight}
      disableHeader={disableHeader}
      onClickBtnGetMore={onClickBtnGetMore}
      isSeeAllButtonVisible={isSeeAllButtonVisible}
      dataTestId={dataTestId}
    >
      <Carousel
        settings={carouselSettings ?? setSettings[format][size]}
        spaceBetweenItems={spaceBetweenItems}
        contentFilter={contentFilter}
        title={title}
      >
        {coversList?.map((book, index) => (
          <Book
            id={book.id}
            key={book.slug}
            slug={book.slug}
            title={book.title}
            author={book.author}
            degreesRange={book.degreesRange}
            hasAudioBook={book.hasAudioBook}
            premiumLock={book.premiumLock}
            imageUrlThumb={book.imageUrlThumb}
            language={book.language}
            size={size}
            format={format}
            showInfo={showInfo}
            contentType={book.contentType}
            onClickBook={() => onClickCover(book, index)}
            moreActionsEvent={() =>
              post_event(
                `${title}/ClickVerMaisAcoesConteudo/${contentFilter}`,
                book.slug
              )
            }
            //TODO these properties are from viewedBook
            percentage={user.canAccessBookProgressBar && book.percentage}
            additionalActions={book.additionalActions}
          />
        ))}
      </Carousel>
    </Shelf>
  ) : null
}

export default BookShelf
